// src/client/renderer/vfx/items/toxic-cloud.ts
//
// Plan 17, Task 3.1 — Toxic Cloud pool.
//
// Triggered by THREE events:
//   1. ItemCastEvent (itemId='toxic_cloud') → phase='cast':
//        Spawns a sickly-green cloud body at the target node.
//        SINGLE-INSTANCE per node — one cloud per nodeId.
//        New cast at the same nodeId before expiry MUST release the prior cloud
//        (F6 release-first contract). Max-active = 6 concurrent nodes.
//
//   2. StatusEffectAppliedEvent (effectType='toxicCloud') → phase='tick':
//        One short green pulse (200ms) at the afflicted player's position.
//        Max-active = 8 concurrent tick pulses across all players.
//
//   3. StatusEffectExpiredEvent (effectType='toxicCloud') → phase='expire':
//        Cloud dissipation — opacity 1→0 over 600ms.
//        Max-active = 8 concurrent expire emitters.
//
// Discriminator pattern for node-keyed Map:
//   Resolver sets args.casterId = event.targetNodeId (the spatial slot identifier,
//   semantically "the nodeId", NOT a player id). Pool keys its cloud-body Map by
//   args.casterId. This semantic abuse of casterId is documented here and accepted
//   because there is no separate "nodeId" field in ItemVfxSpawnArgs, and adding
//   one would require modifying Task 1.1's type (out-of-scope). The comment below
//   the Map declaration also records this choice.
//
// GSAP: use tl.call(fn, undefined, absolutePos) — NOT eventCallback('onComplete').
//
// Registry tick contract (HF1):
//   pool.update(_deltaSeconds) IGNORES dt — does NOT call registry.update().
//   The caller (item-vfx-manager at Task 6.1) advances the shared registry once
//   per frame across all pools.
//
// Audio:
//   Manager fires the item cue only for phase='cast'. Tick and expire visuals
//   are intentionally silent follow-up emitters.

import * as THREE from 'three';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import type {
  GameEvent,
  ItemCastEvent,
  StatusEffectAppliedEvent,
  StatusEffectExpiredEvent,
} from '../../../../shared/game/types';
import { disposeSkillshotGroup } from './skillshot-shared';
import { getItemVfxSmokeTexture, ITEM_VFX_SMOKE_ALPHA_TEST } from './sprite-textures';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Max simultaneously active cloud-body emitters (one per node). */
const MAX_CLOUD_BODIES = 6;

/** Max simultaneously active tick-pulse emitters. */
const MAX_TICK_ACTIVE = 8;

/** Max simultaneously active expire-dissipation emitters. */
const MAX_EXPIRE_ACTIVE = 8;

/** Cloud body colour — sickly green. */
const CLOUD_COLOUR = 0x66cc44;

/** Per-tick pulse colour — lime green. */
const TICK_COLOUR = 0x99ff66;

/** Expire dissipation: opacity fade duration in seconds (600ms). */
const EXPIRE_LIFETIME = 0.6;

/** Tick pulse total lifetime in seconds (200ms). */
const TICK_LIFETIME = 0.2;

/** Initial cast puff lifetime in seconds. Persistent cloud bodies are state-synced by the board. */
const CAST_PUFF_LIFETIME = 0.65;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Emitter builders
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build the short cast puff at the target node position.
 * Persistent cloud bodies are driven from authoritative GameState.activeClouds.
 */
function buildCloudBodyEmitter(
  position: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  const texture = getItemVfxSmokeTexture();
  const sprites: Array<{
    sprite: THREE.Sprite;
    material: THREE.SpriteMaterial;
    base: THREE.Vector3;
  }> = [];
  for (let index = 0; index < 7; index += 1) {
    const angle = (index / 7) * Math.PI * 2;
    const radius = index === 0 ? 0 : 0.2 + (index % 3) * 0.08;
    const material = new THREE.SpriteMaterial({
      map: texture,
      color: index % 2 === 0 ? CLOUD_COLOUR : 0x8be85a,
      transparent: true,
      alphaTest: ITEM_VFX_SMOKE_ALPHA_TEST,
      opacity: 0.48,
      blending: THREE.NormalBlending,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(material);
    const base = new THREE.Vector3(
      position.x + Math.cos(angle) * radius,
      position.y + 0.38 + (index % 2) * 0.08,
      position.z + Math.sin(angle) * radius,
    );
    sprite.position.copy(base);
    const startScale = 0.85 + (index % 3) * 0.12;
    sprite.scale.set(startScale, startScale, 1);
    group.add(sprite);
    sprites.push({ sprite, material, base });
  }

  const handle = registry.create('toxic_cloud_body');
  state.handle = handle;
  const tl = handle.timeline;

  const puff = { value: 0 };
  tl.to(
    puff,
    {
      value: 1,
      duration: CAST_PUFF_LIFETIME,
      ease: 'power2.out',
      onUpdate: () => {
        for (let index = 0; index < sprites.length; index += 1) {
          const { sprite, material, base } = sprites[index]!;
          const angle = (index / sprites.length) * Math.PI * 2;
          const spread = puff.value * (0.45 + (index % 3) * 0.11);
          sprite.position.set(
            base.x + Math.cos(angle) * spread,
            base.y + puff.value * (0.35 + (index % 2) * 0.16),
            base.z + Math.sin(angle) * spread,
          );
          const scale = (0.95 + (index % 3) * 0.14) * (1 + puff.value * 0.9);
          sprite.scale.set(scale, scale, 1);
          material.opacity = (1 - puff.value) * (0.42 + (index % 2) * 0.1);
        }
      },
    },
    0,
  );
  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    CAST_PUFF_LIFETIME,
  );

  return group;
}

/**
 * Build a short per-tick pulse emitter (scale 1.0 → 1.1 → 1.0 over TICK_LIFETIME).
 * Fires finished marker at TICK_LIFETIME seconds.
 */
function buildTickPulseEmitter(
  position: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  const pulseMaterial = new THREE.SpriteMaterial({
    map: getItemVfxSmokeTexture(),
    color: TICK_COLOUR,
    transparent: true,
    alphaTest: ITEM_VFX_SMOKE_ALPHA_TEST,
    opacity: 0.8,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const pulseSprite = new THREE.Sprite(pulseMaterial);
  pulseSprite.position.set(position.x, position.y + 0.5, position.z);
  pulseSprite.scale.set(1.0, 1.0, 1);
  group.add(pulseSprite);

  const handle = registry.create('toxic_cloud_tick');
  state.handle = handle;
  const tl = handle.timeline;

  // Scale: 1.0 → 1.1 (half time) → 1.0 (full time), then finish
  tl.to(
    pulseSprite.scale,
    {
      x: 1.1,
      y: 1.1,
      z: 1,
      duration: TICK_LIFETIME * 0.5,
      ease: 'power1.inOut',
      yoyo: true,
      repeat: 1,
    },
    0,
  );

  // Opacity: slight flash and fade
  const opProxy = { value: 0.8 };
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: TICK_LIFETIME,
      ease: 'power2.in',
      onUpdate: () => {
        pulseMaterial.opacity = opProxy.value;
      },
    },
    0,
  );

  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    TICK_LIFETIME,
  );

  return group;
}

/**
 * Build an expire-dissipation emitter (opacity 1.0 → 0 over EXPIRE_LIFETIME).
 * Fires finished marker at EXPIRE_LIFETIME seconds.
 */
function buildExpireDissipationEmitter(
  position: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  const dissipMaterial = new THREE.SpriteMaterial({
    map: getItemVfxSmokeTexture(),
    color: CLOUD_COLOUR,
    transparent: true,
    alphaTest: ITEM_VFX_SMOKE_ALPHA_TEST,
    opacity: 1.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const dissipSprite = new THREE.Sprite(dissipMaterial);
  dissipSprite.position.set(position.x, position.y + 0.5, position.z);
  dissipSprite.scale.set(2.2, 2.2, 1);
  group.add(dissipSprite);

  const handle = registry.create('toxic_cloud_expire');
  state.handle = handle;
  const tl = handle.timeline;

  // Fade out and scale up slightly
  const opProxy = { value: 1.0 };
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: EXPIRE_LIFETIME,
      ease: 'power1.in',
      onUpdate: () => {
        dissipMaterial.opacity = opProxy.value;
      },
    },
    0,
  );
  tl.to(
    dissipSprite.scale,
    {
      x: 3.0,
      y: 3.0,
      z: 1,
      duration: EXPIRE_LIFETIME,
      ease: 'power1.out',
    },
    0,
  );

  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    EXPIRE_LIFETIME,
  );

  return group;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createToxicCloudPool
// ─────────────────────────────────────────────────────────────────────────────

export function createToxicCloudPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene } = deps;

  /**
   * Cloud-body Map key: args.casterId (semantically the nodeId / spatial-slot key).
   * One active cloud body per node. See discriminator pattern note in file header.
   */
  const cloudBodyMap = new Map<string, { emitter: THREE.Object3D; state: EmitterState }>();

  /** Tick-pulse emitters — one-shot, max-active = MAX_TICK_ACTIVE. */
  const tickStates = new Map<THREE.Object3D, EmitterState>();

  /** Expire-dissipation emitters — one-shot, max-active = MAX_EXPIRE_ACTIVE. */
  const expireStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    const { phase } = args;

    if (phase === 'cast') {
      // ── Cast phase: cloud body per node ─────────────────────────────────
      if (cloudBodyMap.size >= MAX_CLOUD_BODIES) {
        // Only reject if this casterId is NOT already in the map (re-applying same node is allowed)
        const nodeKey = args.casterId ?? '';
        if (!cloudBodyMap.has(nodeKey)) return null;
      }

      const nodeKey = args.casterId ?? '';
      const position = args.casterPosition ?? args.targetPosition ?? { x: 0, y: 0, z: 0 };

      // F6: release-first — if a prior cloud exists for this nodeId, release it
      const prior = cloudBodyMap.get(nodeKey);
      if (prior) {
        prior.state.handle.dispose();
        scene.remove(prior.emitter);
        disposeSkillshotGroup(prior.emitter);
        cloudBodyMap.delete(nodeKey);
      }

      const state: EmitterState = { handle: null!, finished: false };
      const group = buildCloudBodyEmitter(position, deps, state);
      scene.add(group);
      cloudBodyMap.set(nodeKey, { emitter: group, state });
      return group;
    } else if (phase === 'tick') {
      // ── Tick phase: per-tick pulse ───────────────────────────────────────
      if (tickStates.size >= MAX_TICK_ACTIVE) return null;

      const position = args.targetPosition ?? args.casterPosition ?? { x: 0, y: 0, z: 0 };
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildTickPulseEmitter(position, deps, state);
      scene.add(group);
      tickStates.set(group, state);
      return group;
    } else if (phase === 'expire') {
      // ── Expire phase: dissipation ────────────────────────────────────────
      if (expireStates.size >= MAX_EXPIRE_ACTIVE) return null;

      const position = args.targetPosition ?? args.casterPosition ?? { x: 0, y: 0, z: 0 };
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildExpireDissipationEmitter(position, deps, state);
      scene.add(group);
      expireStates.set(group, state);
      return group;
    }

    return null;
  }

  // ── update ───────────────────────────────────────────────────────────────
  // Registry tick contract (HF1): pool.update does NOT advance the registry.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    const all = new Set<THREE.Object3D>();
    for (const { emitter } of cloudBodyMap.values()) {
      all.add(emitter);
    }
    for (const emitter of tickStates.keys()) {
      all.add(emitter);
    }
    for (const emitter of expireStates.keys()) {
      all.add(emitter);
    }
    return all;
  }

  // ── isFinished ───────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    // Cloud body: never self-finishes (long-running); return false while active
    for (const { emitter: ce, state } of cloudBodyMap.values()) {
      if (ce === emitter) return state.finished;
    }
    // Tick pulse
    const tickState = tickStates.get(emitter);
    if (tickState) return tickState.finished;
    // Expire dissipation
    const expireState = expireStates.get(emitter);
    if (expireState) return expireState.finished;
    // Unknown emitter
    return true;
  }

  // ── release ──────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    // Check cloud body map
    for (const [key, { emitter: ce, state }] of cloudBodyMap.entries()) {
      if (ce === emitter) {
        state.handle.dispose();
        scene.remove(emitter);
        disposeSkillshotGroup(emitter);
        cloudBodyMap.delete(key);
        return;
      }
    }
    // Check tick pulses
    const tickState = tickStates.get(emitter);
    if (tickState) {
      tickState.handle.dispose();
      tickStates.delete(emitter);
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
      return;
    }
    // Check expire emitters
    const expireState = expireStates.get(emitter);
    if (expireState) {
      expireState.handle.dispose();
      expireStates.delete(emitter);
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
  }

  // ── dispose (F4) ─────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Dispose all cloud body emitters
    for (const { emitter, state } of cloudBodyMap.values()) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    cloudBodyMap.clear();

    // Dispose all tick-pulse emitters
    for (const [emitter, state] of tickStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    tickStates.clear();

    // Dispose all expire-dissipation emitters
    for (const [emitter, state] of expireStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    expireStates.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolveToxicCloudForEvent
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves Toxic Cloud VFX events.
 *
 * Discriminator pattern for nodeId:
 *   For item_cast events, the resolver sets args.casterId = event.targetNodeId.
 *   This is a semantic repurposing: casterId is "the spatial slot identifier" (the nodeId),
 *   not a player id. The pool uses this value to key its cloud-body Map. Chosen because
 *   ItemVfxSpawnArgs has no dedicated "nodeId" field, and casterId is already a string
 *   identifier slot suitable for this use.
 *
 * Optional position callback:
 *   getPositionForNode?(nodeId) resolves the node's world position for the cloud body.
 *   If omitted, the pool uses { x:0, y:0, z:0 } as fallback (Task 6.1 wires real positions).
 */
export function resolveToxicCloudForEvent(
  event: GameEvent,
  getPositionForNode?: (nodeId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'toxic_cloud'; args: ItemVfxSpawnArgs } | null {
  if (event.type === 'item_cast') {
    if (event.itemId !== 'toxic_cloud') return null;
    const cast = event as ItemCastEvent;
    const nodeId = cast.targetNodeId;
    const position = nodeId && getPositionForNode ? getPositionForNode(nodeId) : undefined;
    return {
      poolId: 'toxic_cloud',
      args: {
        phase: 'cast',
        // Encode nodeId into casterId (spatial slot key — see discriminator pattern above)
        casterId: nodeId,
        casterPosition: position,
      },
    };
  }

  if (event.type === 'status_effect_applied') {
    const applied = event as StatusEffectAppliedEvent;
    if (applied.effectType !== 'toxicCloud') return null;
    return {
      poolId: 'toxic_cloud',
      args: {
        phase: 'tick',
        // targetPosition will be resolved at Task 6.1 via getPositionForPlayer
      },
    };
  }

  if (event.type === 'status_effect_expired') {
    const expired = event as StatusEffectExpiredEvent;
    if (expired.effectType !== 'toxicCloud') return null;
    return {
      poolId: 'toxic_cloud',
      args: {
        phase: 'expire',
        // targetPosition will be resolved at Task 6.1 via getPositionForPlayer
      },
    };
  }

  return null;
}
